/**
 * Screen-time norms for online lessons.
 *
 * Continuous work with screens — Санітарний регламент МОЗ №2205, розділ V п. 8:
 * grade 1 — 10 min; 2–4 — 15; 5–7 — 20; 8–9 — 20–25; 10–11 — 30 on the first lesson, 20 on the second.
 * [М] The 8–9 split (25 on the first lesson, 20 later) mirrors the 10–11 pattern — confirm with
 * the methodologist against the regulation text.
 *
 * Synchronous lessons per day under martial law — наказ МОЗ №1371 (01.08.2022), розділ V п. 10:
 * a school picks ONE option per day (number of lessons × max minutes each).
 */

function assertGrade(grade: number): void {
  if (!Number.isInteger(grade) || grade < 1 || grade > 12) {
    throw new RangeError(`grade must be an integer 1..12, got ${grade}`);
  }
}

export function maxContinuousScreenMinutes(grade: number, lessonOfDay = 1): number {
  assertGrade(grade);
  if (grade === 1) return 10;
  if (grade <= 4) return 15;
  if (grade <= 7) return 20;
  if (grade <= 9) return lessonOfDay <= 1 ? 25 : 20;
  return lessonOfDay <= 1 ? 30 : 20;
}

export interface SyncOption {
  lessons: number;
  minutes: number;
}

const WARTIME_SYNC_OPTIONS: ReadonlyArray<{
  from: number;
  to: number;
  options: ReadonlyArray<SyncOption>;
}> = [
  {
    from: 1,
    to: 2,
    options: [
      { lessons: 2, minutes: 30 },
      { lessons: 3, minutes: 20 },
    ],
  },
  {
    from: 3,
    to: 4,
    options: [
      { lessons: 2, minutes: 45 },
      { lessons: 3, minutes: 30 },
      { lessons: 4, minutes: 20 },
    ],
  },
  {
    from: 5,
    to: 6,
    options: [
      { lessons: 2, minutes: 45 },
      { lessons: 3, minutes: 35 },
      { lessons: 4, minutes: 25 },
    ],
  },
  {
    from: 7,
    to: 9,
    options: [
      { lessons: 2, minutes: 45 },
      { lessons: 3, minutes: 40 },
      { lessons: 4, minutes: 30 },
      { lessons: 5, minutes: 25 },
    ],
  },
  {
    from: 10,
    to: 12,
    options: [
      { lessons: 3, minutes: 45 },
      { lessons: 4, minutes: 35 },
      { lessons: 5, minutes: 30 },
      { lessons: 6, minutes: 25 },
    ],
  },
];

export function wartimeSyncOptions(grade: number): ReadonlyArray<SyncOption> {
  assertGrade(grade);
  const row = WARTIME_SYNC_OPTIONS.find((r) => grade >= r.from && grade <= r.to);
  if (!row) throw new RangeError(`no options for grade ${grade}`);
  return row.options;
}

export interface OnlineDayCheck {
  ok: boolean;
  /** the first allowed option that fits the planned day */
  matched?: SyncOption;
  options: ReadonlyArray<SyncOption>;
}

/** Checks a planned online day (durations of synchronous lessons, minutes) against МОЗ №1371. */
export function checkOnlineDay(
  grade: number,
  syncLessonMinutes: ReadonlyArray<number>,
): OnlineDayCheck {
  const options = wartimeSyncOptions(grade);
  if (syncLessonMinutes.length === 0) return { ok: true, options };
  const longest = Math.max(...syncLessonMinutes);
  const matched = options.find(
    (o) => syncLessonMinutes.length <= o.lessons && longest <= o.minutes,
  );
  return matched ? { ok: true, matched, options } : { ok: false, options };
}
